/**
 * Meta Pixel (Facebook Pixel) tracking utility.
 *
 * Centralises every pixel call so we can:
 *  1. Pass advanced-matching user data with every event.
 *  2. Guarantee value + currency on Purchase events.
 *  3. De-duplicate events (one Purchase per order, etc.).
 *
 * Usage:
 *   import { trackPurchase, trackInitiateCheckout, ... } from '../utils/metaPixel';
 */

const PIXEL_ID = '4267640786824857';

/* ── helpers ─────────────────────────────────────────── */

const getFbq = () => (typeof window !== 'undefined' && window.fbq ? window.fbq : null);

/**
 * Build the advanced-matching (user_data) object that Meta uses to
 * increase Event Match Quality.  All values are **lowercase / trimmed**
 * so the SDK hashes them correctly.
 */
const buildUserData = (params = {}) => {
  const ud = {};

  // email
  if (params.email) ud.em = params.email.trim().toLowerCase();

  // phone – strip non-digits, ensure leading country code
  if (params.phone) {
    let ph = params.phone.replace(/[^0-9]/g, '');
    // If US-style 10-digit, prepend country code
    if (ph.length === 10) ph = '1' + ph;
    if (ph) ud.ph = ph;
  }

  // first name
  if (params.firstName) ud.fn = params.firstName.trim().toLowerCase();

  // last name
  if (params.lastName) ud.ln = params.lastName.trim().toLowerCase();

  return ud;
};

/**
 * Call fbq('init', ...) with advanced matching data so *every subsequent
 * event* on this page load carries the user's PII for server-side matching.
 *
 * Safe to call multiple times – the SDK merges / overwrites.
 */
export const identifyUser = (params = {}) => {
  const fbq = getFbq();
  if (!fbq) return;
  const ud = buildUserData(params);
  if (Object.keys(ud).length === 0) return;
  // Re-init with user data (SDK merges into existing init)
  fbq('init', PIXEL_ID, ud);
};

/* ── standard events ─────────────────────────────────── */

/**
 * PageView – already fired in index.html on first load.
 * Call this on SPA route changes if you want per-page tracking.
 */
export const trackPageView = () => {
  const fbq = getFbq();
  if (!fbq) return;
  fbq('track', 'PageView');
};

/**
 * ViewContent – when a user views a product / ticket page.
 */
export const trackViewContent = ({ contentName, contentCategory, contentIds, value, currency = 'USD', userData = {} } = {}) => {
  const fbq = getFbq();
  if (!fbq) return;
  if (Object.keys(userData).length) identifyUser(userData);
  fbq('track', 'ViewContent', {
    content_name: contentName,
    content_category: contentCategory,
    content_ids: contentIds,
    content_type: 'product',
    value: value || 0,
    currency,
  });
};

/**
 * AddToCart
 */
export const trackAddToCart = ({ contentName, contentIds, value, currency = 'USD', numItems, userData = {} } = {}) => {
  const fbq = getFbq();
  if (!fbq) return;
  if (Object.keys(userData).length) identifyUser(userData);
  fbq('track', 'AddToCart', {
    content_name: contentName,
    content_ids: contentIds,
    content_type: 'product',
    value: value || 0,
    currency,
    num_items: numItems || 1,
  });
};

/**
 * InitiateCheckout
 */
export const trackInitiateCheckout = ({ value, currency = 'USD', numItems, contentIds, userData = {} } = {}) => {
  const fbq = getFbq();
  if (!fbq) return;
  if (Object.keys(userData).length) identifyUser(userData);
  fbq('track', 'InitiateCheckout', {
    value: value || 0,
    currency,
    num_items: numItems || 1,
    content_ids: contentIds,
    content_type: 'product',
  });
};

/**
 * Purchase – THE critical event. Must include value + currency.
 * De-duplicates by eventID (order number) so refreshes don't double-count.
 */
export const trackPurchase = ({ value, currency = 'USD', contentIds, contentName, numItems, orderNumber, userData = {} } = {}) => {
  const fbq = getFbq();
  if (!fbq) return;
  if (!value || value <= 0) return; // Meta requires value > 0

  if (Object.keys(userData).length) identifyUser(userData);

  const params = {
    value: parseFloat(value.toFixed(2)),
    currency,
    content_ids: contentIds,
    content_name: contentName,
    content_type: 'product',
    num_items: numItems || 1,
  };

  // Use eventID for de-duplication (same order won't be counted twice)
  const options = orderNumber ? { eventID: `purchase_${orderNumber}` } : {};

  fbq('track', 'Purchase', params, options);
};

/**
 * CompleteRegistration
 */
export const trackCompleteRegistration = ({ value, currency = 'USD', status, userData = {} } = {}) => {
  const fbq = getFbq();
  if (!fbq) return;
  if (Object.keys(userData).length) identifyUser(userData);
  fbq('track', 'CompleteRegistration', {
    value: value || 0,
    currency,
    status: status || 'complete',
  });
};
